// src/pages/ai/chat.js
import React, { useEffect, useContext, useState, useRef, useMemo } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AppSettings } from '../../config/app-settings.js';
import { Card } from '../../components/card/card.jsx';
import { Icon } from '@iconify/react';
import { createSession } from '../../utils/sessions';
import RecipeCard from '../../components/ai/RecipeCard.jsx';
import MixlistCard from '../../components/ai/MixlistCard.jsx';
import RecipeModal from '../../components/ai/RecipeModal.jsx';
import glassshimmer from "../../assets/images/glassshimmer.png";


import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAICards,
  fetchAISessions,
  // fetchAISessionHistory,
  sendAIMessage,
  addUserMessage,
  clearAIHistory,
  createAISessionOnServer,
  fetchAIAnalyticsHistory
} from '../../actions/ai_actions';

function AiChat() {
	const context = useContext(AppSettings);
	const [activeTab, setActiveTab] = useState("aiChatIntro");
  // const [messages, setMessages] = useState([]); 
  const [inputValue, setInputValue] = useState("");
  const messagesRef = useRef(null);
  const bottomRef = useRef(null);
  // const containerRef = useRef(null)

  const email = localStorage.getItem('email') || '';
  const userInitial = email.charAt(0).toUpperCase();
  const botInitial = 'B';

	// Tracks sessions loaded from Redux or localStorage
	const [localSessions, setLocalSessions] = useState([]);

	// Which session is “open” now
	const [selectedSessionId, setSelectedSessionId] = useState(null);

  const uniqBy = (arr, makeKey) => {
  const seen = new Set();
  const out = [];
    for (const item of arr || []) {
      const k = makeKey(item);
      if (!k || !seen.has(k)) {
        if (k) seen.add(k);
        out.push(item);
      }
    }
    return out;
  };

  //exclude recipes coming from action_cards entirely
  const collectRecipesExcludingActionCards = (msg) => {
    // DO NOT include craft action card recipes anymore
    // const fromCraft = (msg.action_cards || [])
    //   .filter(ac => ac.type === 'craft' && ac.data?.recipe)
    //   .map(ac => ac.data.recipe);

    const fromBarsys = msg.barsys?.recipes || [];
    const fromTop    = Array.isArray(msg.recipes) ? msg.recipes : [];
    const fromAIgen  = Array.isArray(msg.ai_generated_recipe) ? msg.ai_generated_recipe : [];
    const fromLegacy = Array.isArray(msg.barsys_recipe) ? msg.barsys_recipe : [];

    const combined = [
      // ...fromCraft,   // ← removed on purpose
      ...fromBarsys,
      ...fromTop,
      ...fromAIgen,
      ...fromLegacy
    ];

    return uniqBy(
      combined,
      r => (r?.id && String(r.id).toLowerCase()) || (r?.name && r.name.trim().toLowerCase())
    );
  };

  const dedupeMixlists = (mixlists) =>
    uniqBy(mixlists || [], m => (m?.id || m?.name || '').toString().toLowerCase());


	  // -- AI RELATED Redux hooks
	const dispatch = useDispatch();
	const cards            = useSelector(state => state.ai.cards);
	const cardsLoading     = useSelector(state => state.ai.cardsLoading);
	const cardsError       = useSelector(state => state.ai.cardsError);
	const sessions         = useSelector(state => state.ai.sessions);
	const sessionsLoading  = useSelector(state => state.ai.sessionsLoading);
	const sessionsError    = useSelector(state => state.ai.sessionsError);
	const sessionHistory   = useSelector(state => state.ai.analyticsHistory);
	const historyLoading   = useSelector(state => state.ai.analyticsHistoryLoading);
	const historyError     = useSelector(state => state.ai.analyticsHistoryError);
  const totalMessages    = useSelector(state => state.ai.analyticsTotalMessages);
  const loadedTurns     = useSelector(state => state.ai.analyticsLoadedTurns);
  const didInitialScrollRef = useRef(false);

  const showStarterCards = !historyLoading && sessionHistory.length === 0 && cards.length > 0;


  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;
  const prevScrollHeightRef = useRef(0);
  const anchorScrollRestoreRef = useRef(false);
  const canPageUpRef = useRef(false); // <- block "load older" until we finish initial bottom snap

  // Derive a .recipes array on every message
  const historyWithRecipes = useMemo(
    () =>
      sessionHistory.map(msg => {
        // Updated helper excludes action card recipes
        const recipes = collectRecipesExcludingActionCards(msg);

        // Normalize and deduplicate Barsys mixlists
        const barsys = msg.barsys
          ? {
              ...msg.barsys,
              mixlists: dedupeMixlists(msg.barsys.mixlists),
            }
          : msg.barsys;

        return { ...msg, recipes, barsys };
      }),
    [sessionHistory]
  );

	// const messagesRedux    = useSelector(state => state.ai.messages);
	const sendingMessage   = useSelector(state => state.ai.sendingMessage);
  const inputDisabled = sendingMessage;
	const sendError        = useSelector(state => state.ai.sendError);

	const userEmail = localStorage.getItem('email');
	const userId    = userEmail;
  const [selectedRecipe, setSelectedRecipe] = useState(null);


    // Catch all <img> load errors within this page and swap to fallback
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (t && t.tagName === 'IMG' && t.src !== glassshimmer) {
        t.onerror = null;           // prevent loop if fallback fails
        t.src = glassshimmer;       // set fallback
      }
    };
    document.addEventListener('error', handler, true); // use capture to catch <img> errors
    return () => document.removeEventListener('error', handler, true);
  }, []);

  const atBottomRef = useRef(true);
  const lastKeyRef = useRef(''); 

  const makeLastKey = (m) => {
    if (!m) return '';
    // cheap signature; tweak if you like
    return [
      m.from,
      m.text?.length || 0,
      m.created_at || '',
      Array.isArray(m.action_cards) ? m.action_cards.length : 0,
      Array.isArray(m.recipes) ? m.recipes.length : 0,
      Array.isArray(m.barsys?.mixlists) ? m.barsys.mixlists.length : 0
    ].join('|');
  };

  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // queue several snaps to catch async layout & image loads
  const queueBottomSnap = () => {
    const el = messagesRef.current;
    if (!el) return;

    const trySnap = () => {
      if (anchorScrollRestoreRef.current) return; // <- critical
      el.scrollTop = el.scrollHeight;
    };

    requestAnimationFrame(() => {
      trySnap();
      requestAnimationFrame(() => {
        trySnap();
        setTimeout(trySnap, 0);
        setTimeout(trySnap, 120);
      });
    });
  };

  useEffect(() => {
    if (!selectedSessionId) return;
    if (anchorScrollRestoreRef.current) return;   // don't fight prepend anchor restore
    if (historyLoading) return;                   // wait until fetch finishes
    if (sessionHistory.length === 0) return;      // need actual content
    if (didInitialScrollRef.current) return;      // do it once per session

    const el = messagesRef.current;
    if (!el) return;

    // Let DOM + PerfectScrollbar layout finish
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
        el.scrollTop = el.scrollHeight;
        didInitialScrollRef.current = true;
        canPageUpRef.current = true;
      });
    });
  }, [selectedSessionId, historyLoading, sessionHistory.length]);

  // Snap to bottom whenever the *last message* changes
  useEffect(() => {
    if (!selectedSessionId) return;
    if (anchorScrollRestoreRef.current) return; // ignore while paging up

    const last = sessionHistory[sessionHistory.length - 1];
    const key = makeLastKey(last);

    // only act when the *last* bubble identity actually changes
    const isNewLast = key !== lastKeyRef.current;
    lastKeyRef.current = key;
    if (!isNewLast) return; // prepend at top won't change last → do nothing

    // snap rules:
    // - always snap after user's own message
    // - for bot messages, only snap if user was already at bottom
    const shouldSnap =
      last?.from === 'user' ||
      last?.typing ||
      (last?.from === 'bot' && atBottomRef.current);

    if (shouldSnap) queueBottomSnap();
  }, [selectedSessionId, sessionHistory]);

  //Image load snap
  useEffect(() => {
    const root = messagesRef.current;
    if (!root) return;

    const onImgLoad = () => {
      if (anchorScrollRestoreRef.current) return; // skip during top prepend
      if (!atBottomRef.current) return;          // only keep bottom lock if user is at bottom
      queueBottomSnap();
    };

    root.addEventListener('load', onImgLoad, true);
    return () => root.removeEventListener('load', onImgLoad, true);
  }, [selectedSessionId]);

	// on mount: fetch cards + sessions
	useEffect(() => {
    context.setAppContentFullHeight(true);
    context.setAppContentClass('p-3');

    if (userId) {
      dispatch(fetchAICards());
      dispatch(fetchAISessions(userId));
      console.log("sessions from store:", sessions);
    }

    return () => {
      context.setAppContentFullHeight(false);
      context.setAppContentClass('');
    };
	}, [context, dispatch, userId]);

  useEffect(() => {
    if (sessionsLoading || sessionsError !== null) return;

    // normalize to array
    const sessArr = Array.isArray(sessions) ? sessions : [];

    if (sessArr.length === 0) {
      // no past sessions → create one
      const fresh = createSession();
      fresh.title = fresh.id;

      setLocalSessions([fresh]);
      setSelectedSessionId(fresh.id);
      setActiveTab('aiChatNew');
    } else {
      // we have past sessions
      setLocalSessions(sessArr);

      // pick the session with the highest createdAt
      const latest = sessArr.reduce((prev, curr) =>
        curr.createdAt > prev.createdAt ? curr : prev
      );

      setSelectedSessionId(latest.id);
      setActiveTab('aiChatPrev');
      // dispatch(fetchAISessionHistory(latest.id));

      // console.log('Chat History Fetched:', JSON.stringify(sessionHistory));
      dispatch(clearAIHistory());
      dispatch(fetchAIAnalyticsHistory(latest.id, pageSize, 0))
        .then(() => {
          // will re-evaluate once total + history are in the store
      });
    }
  }, [sessionsLoading, sessions, sessionsError, dispatch, userId]);

	// milliseconds in a day
	const MS_DAY = 24 * 60 * 60 * 1000;
	const now = Date.now();

	const buckets = {
	today: [],
	week: [],
	twoWeeks: [],
	};

	localSessions.forEach(s => {
	const age = now - s.createdAt;
	if (age < MS_DAY) buckets.today.push(s);
	else if (age < 7 * MS_DAY) buckets.week.push(s);
	else if (age < 14 * MS_DAY) buckets.twoWeeks.push(s);
	});

  const toggleAiTab = (e, tab, payload) => {
    // stop any default <a> behavior
    if (e && e.preventDefault) e.preventDefault();

    // 1) update your active‐tab
    setActiveTab(tab);

    // 2) if clicking an existing session, set it & fetch history
    if (tab === 'aiChatPrev' && typeof payload === 'string') {
      setSelectedSessionId(payload);

      // reset scroll flags for the new session
      didInitialScrollRef.current = false;
      anchorScrollRestoreRef.current = false;
      canPageUpRef.current = false;   
      // dispatch(fetchAISessionHistory(payload));
      dispatch(clearAIHistory());
      dispatch(fetchAIAnalyticsHistory(payload, pageSize, 0));
    }

    // 3) starting a brand‐new chat (no prompt)
    if (tab === 'aiChatNew' && payload == null) {
      const fresh = createSession();
      fresh.title = fresh.id;
      setLocalSessions(s => [fresh, ...s]);
      setSelectedSessionId(fresh.id);
      didInitialScrollRef.current = false;
      anchorScrollRestoreRef.current = false;
      canPageUpRef.current = false;


      dispatch(clearAIHistory());
      dispatch(createAISessionOnServer(userId, fresh.id))
        .catch(err => console.warn('Backend session creation failed:', err));
    }

    // 4) sending a prompt in “New Chat”
    if (tab === 'aiChatNew' && typeof payload === 'string') {
      dispatch(addUserMessage(payload));

      if (!selectedSessionId) {
        // no session yet → create one first
        const fresh = createSession();
        fresh.title = fresh.id;
        setLocalSessions(s => [fresh, ...s]);
        setSelectedSessionId(fresh.id);
        dispatch(sendAIMessage({
          user_id:    userId,
          session_id: fresh.id,
          input:      { text: payload },
          metadata:   { platform: 'demo_ui' }
        }));
        return;
      }

      // existing session → just send
      dispatch(sendAIMessage({
        user_id:    userId,
        session_id: selectedSessionId,
        input:      { text: payload },
        metadata:   { platform: 'demo_ui' }
      }));
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text) return;

    // 1) Immediately show the user bubble locally
    dispatch(addUserMessage(text));

    queueBottomSnap();
    // console.log('BEFORE SENDING AI MESSAGE:', userId, selectedSessionId, text);

    // 2) Send it to your backend
    dispatch(sendAIMessage({
      user_id:    userId,
      session_id: selectedSessionId,
      input:      { text },            // your API payload
      metadata:   { platform: 'demo_ui' }
    }));

    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  useEffect(() => {
		context.setAppContentFullHeight(true);
		context.setAppContentClass('p-3');
		
		return function cleanUp() {
			context.setAppContentFullHeight(false);
			context.setAppContentClass('');
		}
		
		// eslint-disable-next-line
	}, []);

  useEffect(() => {
    setHasMore(loadedTurns < totalMessages);
  }, [loadedTurns, totalMessages]);
  
  useEffect(() => {
    if (!anchorScrollRestoreRef.current) return;
    const el = messagesRef.current;
    if (!el) return;
    // wait for DOM paint to reflect new nodes
    setTimeout(() => {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.scrollTop = delta; // keep the same visual anchor
      anchorScrollRestoreRef.current = false;
    }, 0);
  }, [sessionHistory]); // runs after prepend

	return (
		<Card className="h-100">
			<div className="d-flex h-100">
        {selectedRecipe && (
          <RecipeModal
            recipe={selectedRecipe}
            onClose={() => setSelectedRecipe(null)}
          />
        )}
				<a className="d-lg-none btn btn-lg bg-inverse bg-opacity-15 bg-blur-2 text-body rounded-0 z-3 ms-1px mt-5 shadow-lg position-absolute top-0 start-0 " data-bs-toggle="offcanvas" href="#aiChatSidebar" role="button" aria-controls="offcanvasExample">
					<i className="fa fa-gear"></i>
				</a>
				<div className="w-300px offcanvas offcanvas-start bg-none position-lg-relative transform-lg-none visibility-lg-initial z-lg-3" id="aiChatSidebar">
					<div className="d-lg-none bg-blur-3 d-block position-absolute top-0 end-0 w-100 h-100"></div>
					
					<div className="position-relative h-100 d-flex flex-column">
						<div className="p-3">
              <button
                type="button"
                className="btn btn-outline-theme w-100 rounded-3 py-2 d-flex align-items-center justify-content-center"
                onClick={e => {
                  e.preventDefault();
                  toggleAiTab(e, 'aiChatNew', null);
                }}
              >
                New Chat
              </button>
						</div>
						
						<hr className="m-0 opacity-20" />
						
						<div className="flex-1 overflow-hidden text-body">
              <PerfectScrollbar className="p-3 h-100">
                {sessionsLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-theme" role="status">
                      <span className="visually-hidden">Loading sessions...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* TODAY */}
                    {buckets.today.length > 0 && (
                      <>
                        <div className="fw-500 mb-1 d-flex align-items-center">
                          <Icon icon="solar:calendar-bold-duotone"
                                className="fs-16px me-8px mt-n1 text-theme" />
                          Today
                        </div>
                        <div className="px-2">
                          {buckets.today.map(s => (
                            <div
                              key={s.id}
                              className={`position-relative hover-bg-light bg-opacity-25 hover-show rounded-2 py-6px px-3 border-0 d-flex align-items-center${selectedSessionId === s.id ? ' bg-light' : ''}`}
                            >
                                <div className="flex-1 text-body text-opacity-75">
                                {s.title}
                              </div>
                              <a
                                href="#/"
                                className="stretched-link text-decoration-none"
                                data-toggle-ai-tab="aiChatPrev"
                                onClick={e => { e.preventDefault(); toggleAiTab(e, 'aiChatPrev', s.id); setSelectedSessionId(s.id);}}
                              >&nbsp;</a>
                              {/* … dropdown … */}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* 1 WEEK AGO */}
                    {buckets.week.length > 0 && (
                      <>
                        <div className="fw-500 mb-1 d-flex align-items-center mt-3">
                          <Icon icon="solar:calendar-bold-duotone"
                                className="fs-16px me-8px mt-n1 text-theme" />
                          1 Week Ago
                        </div>
                        <div className="px-2">
                          {buckets.week.map(s => (
                            <div key={s.id}
                                className={`position-relative hover-bg-light bg-opacity-25 hover-show
                                            rounded-2 py-6px px-3 border-0 d-flex align-items-center${selectedSessionId === s.id ? ' bg-light' : ''}`}>
                              <div className="flex-1 text-body text-opacity-75">
                                {s.title}
                              </div>
                              <a
                                href="#/"
                                className="stretched-link text-decoration-none"
                                data-toggle-ai-tab="aiChatPrev"
                                onClick={e => { e.preventDefault(); toggleAiTab(e, 'aiChatPrev', s.id); }}
                              >&nbsp;</a>
                              {/* … dropdown … */}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* 2 WEEKS AGO */}
                    {buckets.twoWeeks.length > 0 && (
                      <>
                        <div className="fw-500 mb-1 d-flex align-items-center mt-3">
                          <Icon icon="solar:calendar-bold-duotone"
                                className="fs-16px me-8px mt-n1 text-theme" />
                          2 Weeks Ago
                        </div>
                        <div className="px-2">
                          {buckets.twoWeeks.map(s => (
                            <div key={s.id}
                                className={`position-relative hover-bg-light bg-opacity-25 hover-show
                                            rounded-2 py-6px px-3 border-0 d-flex align-items-center${selectedSessionId === s.id ? ' bg-light' : ''}`}
                            >
                              <div className="flex-1 text-body text-opacity-75">
                                {s.title}
                              </div>
                              <a
                                href="#/"
                                className="stretched-link text-decoration-none"
                                data-toggle-ai-tab="aiChatPrev"
                                onClick={e => { e.preventDefault(); toggleAiTab(e, 'aiChatPrev', s.id); setSelectedSessionId(s.id); }}
                              >&nbsp;</a>
                              {/* … dropdown … */}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* EARLIER (optional) */}
                    {(() => {
                      const earlier = localSessions.filter(s =>
                        !buckets.today.includes(s) &&
                        !buckets.week.includes(s) &&
                        !buckets.twoWeeks.includes(s)
                      );
                      if (earlier.length === 0) return null;
                      return (
                        <>
                          <div className="fw-500 mb-1 d-flex align-items-center mt-3">
                            <Icon icon="solar:calendar-bold-duotone"
                                  className="fs-16px me-8px mt-n1 text-theme" />
                            Earlier
                          </div>
                          <div className="px-2">
                            {earlier.map(s => (
                              <div key={s.id}
                                    className={`position-relative hover-bg-light bg-opacity-25 hover-show
                                                rounded-2 py-6px px-3 border-0 d-flex align-items-center${selectedSessionId === s.id ? ' bg-light' : ''}`}
                              >
                                <div className="flex-1 text-body text-opacity-75">
                                  {s.title}
                                </div>
                                <a
                                  href="#/"
                                  className="stretched-link text-decoration-none"
                                  data-toggle-ai-tab="aiChatPrev"
                                  onClick={e => { e.preventDefault(); toggleAiTab(e, 'aiChatPrev', s.id); setSelectedSessionId(s.id);}}
                                >&nbsp;</a>
                                {/* … dropdown … */}
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </PerfectScrollbar>
            </div>
						
						<hr className="m-0 opacity-20" />

					</div>
				</div>

        <div className="flex-1 d-flex flex-column h-100 mw-100">
          {/* Message area or action cards */}
          <div className="flex-1 d-flex flex-column overflow-hidden">
            <PerfectScrollbar
              key={selectedSessionId}
              containerRef={el => (messagesRef.current = el)}
              onUpdate={() => {
                const el = messagesRef.current;
                if (!el) return;

                // If we’re restoring a top-prepend anchor, do nothing here
                if (anchorScrollRestoreRef.current) return;

                // Snap to bottom either (a) first time this session or (b) user is already at bottom
                if (!didInitialScrollRef.current || atBottomRef.current) {
                  scrollToBottom();
                  if (!didInitialScrollRef.current) {
                    didInitialScrollRef.current = true;
                    canPageUpRef.current = true;
                  }
                }
              }}              
              onScrollY={(container) => {
                if (!container) return;

                // keep bottom-lock state updated
                const nearBottom =
                  container.scrollHeight - (container.scrollTop + container.clientHeight) <= 24;
                atBottomRef.current = nearBottom;

                // fetch older when user nears the very top
                if (!selectedSessionId || historyLoading) return;
                if (!canPageUpRef.current) return;     // only after we’ve done initial bottom snap
                if (!hasMore) return;
                if (container.scrollTop > 24) return;  // only near the very top

                const el = messagesRef.current;
                if (!el) return;
                if (el.scrollHeight <= el.clientHeight) return; // nothing to scroll

                prevScrollHeightRef.current = el.scrollHeight; // for anchor restore
                anchorScrollRestoreRef.current = true;

                const nextOffset = loadedTurns; // use turns count, not bubble count
                dispatch(fetchAIAnalyticsHistory(selectedSessionId, pageSize, nextOffset));
              }}
                          className="flex-1 p-2 p-lg-4"
            >
              {/* ADDED: spinner while session history is loading */}
              {historyLoading && (
                <div className="d-flex justify-content-center align-items-center w-100 my-4">
                  <div className="spinner-border text-theme" role="status">
                    <span className="visually-hidden">Loading conversation...</span>
                  </div>
                </div>
              )}

              {/* ── Always Show Static AI Cards ── */}
              {showStarterCards && (
                <div className="row g-4 mb-4">
                  {cards.map(card => (
                    <div className="col-sm-6 col-xl-4" key={card.id}>
                      <a
                        href="#/"
                        className="card cursor-pointer h-100 text-decoration-none text-body text-opacity-75"
                        onClick={e => {
                          e.preventDefault();
                          toggleAiTab(e, 'aiChatNew', card.prompt);
                        }}
                      >
                        <div className="card-body p-4">
                          <div className="mb-3 d-flex">
                            <Icon icon={card.icon} className="fs-40px" />
                          </div>
                          <div className="fw-500 text-body mb-1">{card.title}</div>
                          <div>{card.prompt}</div>
                        </div>
                        <div className="card-arrow">
                          <div className="card-arrow-top-left"></div>
                          <div className="card-arrow-top-right"></div>
                          <div className="card-arrow-bottom-left"></div>
                          <div className="card-arrow-bottom-right"></div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Chat History + Rich Bot Responses ── */}
              {historyWithRecipes.map((msg, idx) => (
                <React.Fragment key={idx}>
                  {/* 1) Chat Bubble */}
                  <div
                    className={`d-flex align-items-center mb-3 ${
                      msg.from === 'user' ? 'justify-content-end' : 'justify-content-start'
                    }`}
                  >
                    {msg.from !== 'user' ? (
                      <>
                        <div
                          className="w-30px h-30px me-2 fs-20px rounded-circle
                                    bg-theme bg-opacity-15 text-theme
                                    d-flex align-items-center justify-content-center"
                        >
                          {botInitial}
                        </div>
                        <div
                          className="d-inline-flex align-items-center rounded-4 px-3 py-2
                                    bg-theme text-white"
                          style={{ maxWidth: '60%', wordBreak: 'break-word' }}
                        >
                          {msg.text}
                          {/* If this is a typing placeholder, show animated dots */}
                          {msg.typing ? (
                            <span className="typing-indicator">
                              <span className="dot" />
                              <span className="dot" />
                              <span className="dot" />
                            </span>
                          ) : (
                            msg.text
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="d-inline-flex align-items-center rounded-4 px-3 py-2
                                    bg-inverse bg-opacity-10 text-body"
                          style={{ maxWidth: '60%', wordBreak: 'break-word' }}
                        >
                          {msg.text}
                        </div>
                        <div
                          className="w-30px h-30px ms-2 fs-20px rounded-circle
                                    text-white bg-inverse bg-opacity-50
                                    d-flex align-items-center justify-content-center"
                        >
                          {userInitial}
                        </div>
                      </>
                    )}
                  </div>

                    {/* 2) Mixlist Grid */}
                    {msg.barsys?.mixlists?.length > 0 && (
                      <div className="row g-4 mb-4" style={{ paddingLeft: 17 }}>
                        {msg.barsys.mixlists.map(mixlist => (
                          <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-2 mb-3" key={mixlist.id}>
                            <MixlistCard mixlist={mixlist} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 3) Recipe Grid */}
                    {msg.recipes.length > 0 && (
                      <div className="row g-0 mb-3">
                        {msg.recipes.map(recipe => (
                          <div className="col-6 col-sm-4 col-md-3 p-1" key={recipe.name}>
                            <RecipeCard 
                              recipe={recipe} 
                              onClick={r => setSelectedRecipe(r) }
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 4) Dynamic Action Cards */}
                    {msg.action_cards
                      ?.filter(ac => ac.type !== 'craft' && ac.type !== 'device')
                      .length > 0 && (
                      <div className="row g-2 mb-4">
                        {msg.action_cards
                          .filter(ac => ac.type !== 'craft' && ac.type !== 'device')
                          .map(ac => (
                            <div className="col-auto" key={ac.action_id}>
                              <button
                                className="btn btn-outline-theme"
                                onClick={() => toggleAiTab(null, 'aiChatNew', ac.value)}
                              >
                                {ac.label}
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
               <div ref={bottomRef} />
            </PerfectScrollbar>
          </div>

          {/* Input bar */}
          <div>
            <div className="px-lg-4 px-3 pb-3">
              <div className="position-relative mx-lg-4 mx-2 h-100">
                <div className="position-absolute start-0 bottom-0 p-2">
                  <button type="button" className="btn btn-lg px-1 py-1 text-body text-opacity-50">
                    <i className="fa fa-fw fa-plus"></i>
                  </button>
                  <button type="button" className="btn btn-lg px-1 py-1 text-body text-opacity-50">
                    <i className="fa fa-fw fa-globe"></i>
                  </button>
                  <button type="button" className="btn btn-lg px-1 py-1 text-body text-opacity-50">
                    <i className="fa fa-fw fa-microphone"></i>
                  </button>
                </div>

                <input
                  type="text"
                  className="form-control form-control-lg shadow-none rounded-0"
                  style={{ paddingBottom: '60px', paddingTop: '10px' }}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  disabled={inputDisabled}
                  id="userInput"
                  placeholder="Message AI Bot"
                />

                <div className="position-absolute end-0 bottom-0 px-2 pb-2">
                  <button
                    id="sendButton"
                    disabled={inputDisabled}
                    onClick={handleSendMessage}
                    className="btn btn-lg bg-inverse text-inverse bg-opacity-20 rounded-0"
                  >
                    <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
			</div>
		</Card>
	)
}

export default AiChat;